import React, { useEffect, useState } from "react";
import { StyleSheet, Text, View } from "react-native";

import { useGameplayStore } from "../../game/stores/useGameplayStore";
import { useAssetStore } from "../../stores/useAssetStore";
import { useDevSession } from "../../dev/devSession";
import { formatFrameStats, frameStats } from "../../dev/frameStats";
import { formatNodeDebug, nodeDebug } from "../../dev/graphDebug";
import { useOptionHover } from "../board/options";
import { DEV_COLOR } from "./devStyles";

/** requestAnimationFrame deltas over the last ~60 frames, while mounted. */
function useFrameStats(): string {
  const [text, setText] = useState("…");
  useEffect(() => {
    const raf = globalThis.requestAnimationFrame;
    if (typeof raf !== "function") return undefined;
    const deltas: number[] = [];
    let last = 0;
    let id = 0;
    let frames = 0;
    const tick = (t: number) => {
      if (last) deltas.push(t - last);
      if (deltas.length > 60) deltas.shift();
      last = t;
      if (++frames % 15 === 0) setText(formatFrameStats(frameStats(deltas)));
      id = raf(tick);
    };
    id = raf(tick);
    return () => globalThis.cancelAnimationFrame?.(id);
  }, []);
  return text;
}

const Fps: React.FC = () => <Text style={styles.text}>{useFrameStats()}</Text>;

/** Hovered word (a chip or option node) and the current word, with edge facts. */
const HoverInfo: React.FC = () => {
  const graph = useAssetStore((s) => s.graphData);
  const current = useGameplayStore((s) => s.currentWord);
  const hovered = useOptionHover((s) => s.hovered);
  const cur = nodeDebug(graph, current);
  const hov = nodeDebug(graph, hovered, current);
  return (
    <>
      {cur && <Text style={styles.text}>current: {formatNodeDebug(cur)}</Text>}
      <Text style={styles.text}>
        hover: {hov ? formatNodeDebug(hov) : "(hover a word button)"}
      </Text>
    </>
  );
};

/** Dev overlays in the board's bottom-left corner: FPS and hover debug. */
const DevHud: React.FC = () => {
  const showFps = useDevSession((s) => s.showFps);
  const hoverDebug = useDevSession((s) => s.hoverDebug);
  if (!showFps && !hoverDebug) return null;
  return (
    <View pointerEvents="none" style={styles.box} testID="dev-hud">
      {showFps && <Fps />}
      {hoverDebug && <HoverInfo />}
    </View>
  );
};

const styles = StyleSheet.create({
  box: {
    position: "absolute",
    left: 6,
    bottom: 6,
    maxWidth: "90%",
    padding: 4,
    borderRadius: 6,
    borderWidth: 1,
    borderStyle: "dashed",
    borderColor: DEV_COLOR,
    backgroundColor: "rgba(18,18,18,0.85)",
  },
  text: { color: "#D0D4D9", fontSize: 10, fontFamily: "monospace" },
});

export default DevHud;
